import type { CharacterDefinition, HomeArtPlacement } from "../../data/characters";
import type { PerfMonitor } from "../../perf/perf-monitor";

export interface HomeViewCallbacks {
  onPrevious: () => void;
  onNext: () => void;
  onSelectCharacter: (index: number) => void;
  onPlay: () => void;
  onFormation: () => void;
}

export class HomeView {
  readonly element: HTMLElement;
  private readonly background: HTMLImageElement;
  private readonly characterArt: HTMLElement;
  private readonly characterLow: HTMLImageElement;
  private readonly characterHigh: HTMLImageElement;
  private readonly characterIconButtons: HTMLButtonElement[];
  private readonly name: HTMLElement;
  private readonly tagline: HTMLElement;
  private readonly dialogue: HTMLElement;
  private readonly characterLevel: HTMLElement;
  private readonly characterBond: HTMLElement;
  private readonly loading: HTMLElement;
  private readonly playButton: HTMLButtonElement;
  private readonly performancePanel: HTMLElement;
  private readonly toast: HTMLElement;
  private listeners: Array<() => void> = [];
  private toastTimer: number | undefined;
  private lowSwapRequest = 0;

  constructor(
    root: HTMLElement,
    character: CharacterDefinition,
    allCharacters: CharacterDefinition[],
    callbacks: HomeViewCallbacks,
    private readonly monitor: PerfMonitor,
    initialLowImage = character.home.lowImage,
  ) {
    const characterPicker = allCharacters
      .map(
        (entry, index) =>
          `<button class="character-icon-button" type="button" data-character-index="${index}" aria-label="Select ${entry.name}" aria-pressed="false"><img src="${entry.icon}" alt="" decoding="async" /><span>${String(index + 1).padStart(2, "0")}</span></button>`,
      )
      .join("");
    this.element = document.createElement("main");
    this.element.className = "home-screen scene-shell";
    this.element.innerHTML = `
      <img class="home-background" alt="" aria-hidden="true" />
      <div class="home-scrim" aria-hidden="true"></div>
      <div class="home-atmosphere" aria-hidden="true">
        <span class="atmosphere-grid"></span>
        <span class="atmosphere-line atmosphere-line-one"></span>
        <span class="atmosphere-line atmosphere-line-two"></span>
        <i class="atmosphere-particle particle-one"></i>
        <i class="atmosphere-particle particle-two"></i>
        <i class="atmosphere-particle particle-three"></i>
      </div>
      <div class="character-art" aria-hidden="true">
        <div class="character-art-stage">
          <div class="character-glow"></div>
          <img class="character-image character-low" alt="" />
          <img class="character-image character-high" alt="" />
          <div class="character-shadow"></div>
        </div>
      </div>
      <div class="home-overlay">
        <header class="home-header">
          <div class="utility-cluster" aria-label="Utilities">
            <button class="utility-button" type="button" data-utility="Menu" aria-label="Open menu"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h10" /></svg></button>
            <button class="utility-button has-badge" type="button" data-utility="Mail" aria-label="Open mail"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6h16v12H4zM4 7l8 6 8-6" /></svg><b>3</b></button>
            <button class="utility-button has-badge" type="button" data-utility="Notices" aria-label="Open notices"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 17h12l-1.5-2.2V10a4.5 4.5 0 0 0-9 0v4.8zM10 20h4" /></svg><b>1</b></button>
            <button class="utility-button" type="button" data-utility="Calendar" aria-label="Open calendar"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 7h14v13H5zM8 4v5M16 4v5M5 11h14" /></svg></button>
            <span class="header-brand"><span class="eyebrow-dot"></span>STARBOUND</span>
          </div>
          <div class="player-status" aria-label="Player status">
            <div class="player-level"><span class="player-avatar">✦</span><span><b>LV. 12</b><small>RIN</small></span></div>
            <div class="resource-chip stamina-chip"><span><small>STAMINA</small><b>45 / 120</b></span><i class="stamina-bar"><em></em></i></div>
            <div class="resource-chip"><span class="resource-gem">◆</span><b>12,480</b></div>
            <div class="resource-chip resource-chip-small"><span class="resource-orb">●</span><b>86</b></div>
          </div>
        </header>
        <div class="home-main">
          <section class="character-showcase" aria-live="polite">
            <div class="character-copy">
              <p class="character-kicker">CURRENT COMPANION <span class="kicker-line"></span></p>
              <h1 class="character-name"></h1>
              <p class="character-tagline"></p>
              <p class="character-dialogue"></p>
              <div class="character-stats"><span><small>LEVEL</small><b class="character-level"></b></span><span><small>BOND</small><b class="character-bond"></b></span></div>
            </div>
            <div class="character-nav" aria-label="Character navigation">
              <div class="character-picker" role="group" aria-label="Select companion">${characterPicker}</div>
              <div class="character-nav-controls">
                <button class="icon-button previous-button" type="button" aria-label="Previous character">←</button>
                <div class="character-position" aria-hidden="true"></div>
                <button class="icon-button next-button" type="button" aria-label="Next character">→</button>
              </div>
            </div>
          </section>
          <aside class="home-actions" aria-label="Home actions">
            <button class="play-button" type="button"><span class="play-copy"><small>OPERATION</small><strong>QUICK FLIGHT</strong><em>STAGE 01 <b>·</b> BEST 12</em></span><span class="play-arrow">↗</span></button>
            <aside class="event-banner" aria-label="Limited event">
              <span class="event-kicker">LIMITED EVENT</span>
              <strong>NIGHT FLIGHT</strong>
              <small><i></i> 6 DAYS LEFT <b>／ LIVE</b></small>
              <span class="event-arrow">↗</span>
            </aside>
            <nav class="secondary-nav" aria-label="Home navigation">
              <button class="tile-squad" type="button"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="8" r="3"/><circle cx="16" cy="9" r="2.3"/><path d="M3.5 20c.6-3.6 2.4-5.5 5.5-5.5s4.9 1.9 5.5 5.5M14 15c3.1-.2 5 1.3 6 5"/></svg><span>SQUAD</span><i>↗</i></button>
              <button class="tile-character" type="button" data-coming-soon="CHARACTER"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="8" r="3"/><path d="M5 20c.8-3.5 3.2-5.5 7-5.5s6.2 2 7 5.5"/></svg><span>CHARACTER</span><i>↗</i></button>
              <button class="tile-missions has-tile-badge" type="button" data-coming-soon="MISSIONS"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 5 14 7-14 7 3-7z"/></svg><span>MISSIONS</span><b>2</b><i>↗</i></button>
              <button class="tile-base has-tile-badge" type="button" data-coming-soon="BASE"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m4 11 8-6 8 6v8H4zM9 19v-5h6v5"/></svg><span>BASE</span><b>!</b><i>↗</i></button>
              <button class="tile-shop has-tile-badge" type="button" data-coming-soon="SHOP"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 9h14l-1 11H6zM8 9a4 4 0 0 1 8 0"/></svg><span>SHOP</span><b>NEW</b><i>↗</i></button>
              <button class="tile-archive" type="button" data-coming-soon="ARCHIVE"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 7h14v12H5zM7 4h10v3H7zM9 11h6"/></svg><span>ARCHIVE</span><i>↗</i></button>
            </nav>
            <p class="loading-status" role="status" aria-live="polite"></p>
          </aside>
        </div>
        <footer class="home-footer"><span><span class="online-dot"></span> ONLINE</span><span>DAILY RESET <b>04:00</b></span><span>SEASON 01 <i>/ SKYBOUND</i></span></footer>
      </div>
      <div class="home-toast" role="status" aria-live="polite" aria-hidden="true"></div>
      <div class="debug-performance" hidden></div>
    `;
    root.replaceChildren(this.element);

    this.background = this.element.querySelector<HTMLImageElement>(".home-background")!;
    this.characterArt = this.element.querySelector<HTMLElement>(".character-art")!;
    this.characterLow = this.element.querySelector<HTMLImageElement>(".character-low")!;
    this.characterHigh = this.element.querySelector<HTMLImageElement>(".character-high")!;
    this.characterIconButtons = Array.from(this.element.querySelectorAll<HTMLButtonElement>(".character-icon-button"));
    this.characterIconButtons.forEach((button, index) => button.style.setProperty("--icon-accent", allCharacters[index].accent));
    this.name = this.element.querySelector<HTMLElement>(".character-name")!;
    this.tagline = this.element.querySelector<HTMLElement>(".character-tagline")!;
    this.dialogue = this.element.querySelector<HTMLElement>(".character-dialogue")!;
    this.characterLevel = this.element.querySelector<HTMLElement>(".character-level")!;
    this.characterBond = this.element.querySelector<HTMLElement>(".character-bond")!;
    this.loading = this.element.querySelector<HTMLElement>(".loading-status")!;
    this.playButton = this.element.querySelector<HTMLButtonElement>(".play-button")!;
    this.performancePanel = this.element.querySelector<HTMLElement>(".debug-performance")!;
    this.toast = this.element.querySelector<HTMLElement>(".home-toast")!;

    this.listeners.push(this.listen(".previous-button", "click", callbacks.onPrevious));
    this.listeners.push(this.listen(".next-button", "click", callbacks.onNext));
    this.listeners.push(this.listen(".play-button", "click", callbacks.onPlay));
    this.listeners.push(this.listen(".tile-squad", "click", callbacks.onFormation));
    this.characterIconButtons.forEach((button) => {
      const onClick = () => callbacks.onSelectCharacter(Number(button.dataset.characterIndex));
      button.addEventListener("click", onClick);
      this.listeners.push(() => button.removeEventListener("click", onClick));
    });
    this.element.querySelectorAll<HTMLButtonElement>("[data-coming-soon]").forEach((button) => {
      const onClick = () => this.showToast(`${button.dataset.comingSoon} — coming soon`);
      button.addEventListener("click", onClick);
      this.listeners.push(() => button.removeEventListener("click", onClick));
    });
    this.element.querySelectorAll<HTMLButtonElement>("[data-utility]").forEach((button) => {
      const onClick = () => this.showToast(`${button.dataset.utility} — coming soon`);
      button.addEventListener("click", onClick);
      this.listeners.push(() => button.removeEventListener("click", onClick));
    });
    this.setCharacter(character, false, initialLowImage);
  }

  private listen(selector: string, event: string, callback: () => void): () => void {
    const element = this.element.querySelector<HTMLElement>(selector)!;
    element.addEventListener(event, callback);
    return () => element.removeEventListener(event, callback);
  }

  setBackground(url: string): void {
    this.background.src = url;
  }

  setCharacter(character: CharacterDefinition, transition = true, lowImage = character.home.lowImage): void {
    this.setCharacterMetadata(character);
    this.showCharacterLow(lowImage, transition, character.home.placement);
  }

  setCharacterMetadata(character: CharacterDefinition): void {
    this.name.textContent = character.name;
    this.tagline.textContent = character.subtitle ?? character.tagline;
    this.dialogue.textContent = character.dialogue ?? this.dialogueFor(character.id);
    const stats = this.statsFor(character.id);
    this.characterLevel.textContent = stats.level;
    this.characterBond.textContent = stats.bond;
    this.element.style.setProperty("--character-accent", character.accent);
    this.characterLow.alt = `${character.name}, ${character.tagline}`;
    this.characterHigh.alt = `${character.name}, ${character.tagline}`;
  }

  private setPlacement(placement: HomeArtPlacement): void {
    this.characterArt.style.setProperty("--character-x", `${placement.x}px`);
    this.characterArt.style.setProperty("--character-y", `${placement.y}px`);
    this.characterArt.style.setProperty("--character-scale", String(placement.scale));
    this.characterArt.style.setProperty("--character-anchor-x", `${placement.anchorX * -100}%`);
    this.characterArt.style.setProperty("--character-anchor-y", `${placement.anchorY * -100}%`);
    this.characterArt.style.setProperty("--character-origin-x", `${placement.anchorX * 100}%`);
    this.characterArt.style.setProperty("--character-origin-y", `${placement.anchorY * 100}%`);
  }

  showCharacterLow(url: string, transition = true, placement?: HomeArtPlacement): void {
    const request = ++this.lowSwapRequest;
    const show = () => {
      if (request !== this.lowSwapRequest) return;
      if (placement) this.setPlacement(placement);
      this.characterLow.src = url;
      this.characterLow.classList.add("is-visible");
      this.characterHigh.classList.remove("is-visible");
    };
    if (!transition) {
      show();
      return;
    }
    const preloader = new Image();
    preloader.decoding = "async";
    preloader.onload = show;
    preloader.src = url;
  }

  private dialogueFor(characterId: string): string {
    const dialogue: Record<string, string> = {
      alice: "The night is wide open. Shall we take the long way?",
      beatrice: "The clouds are calm today. Let’s make a little mischief.",
      chloe: "I marked a new route across the stars. Follow me.",
    };
    return dialogue[characterId] ?? "Ready when you are.";
  }

  private statsFor(characterId: string): { level: string; bond: string } {
    const stats: Record<string, { level: string; bond: string }> = {
      alice: { level: "08", bond: "72%" },
      beatrice: { level: "11", bond: "84%" },
      chloe: { level: "06", bond: "58%" },
    };
    return stats[characterId] ?? { level: "01", bond: "0%" };
  }

  swapCharacterImage(url: string, transition = true, placement?: HomeArtPlacement): void {
    const nextLayer = this.characterHigh;
    nextLayer.src = url;
    const show = () => {
      if (placement) this.setPlacement(placement);
      nextLayer.classList.toggle("is-visible", true);
      this.characterLow.classList.toggle("is-visible", false);
      nextLayer.removeEventListener("load", show);
    };
    if (!transition || nextLayer.complete) show();
    else nextLayer.addEventListener("load", show, { once: true });
  }

  setPosition(index: number, total: number): void {
    const position = this.element.querySelector<HTMLElement>(".character-position")!;
    position.textContent = `${String(index + 1).padStart(2, "0")} / ${String(total).padStart(2, "0")}`;
    this.characterIconButtons.forEach((button, buttonIndex) => {
      const selected = buttonIndex === index;
      button.classList.toggle("is-selected", selected);
      button.setAttribute("aria-pressed", String(selected));
    });
  }

  setLoading(message: string): void {
    this.loading.textContent = message;
  }

  private showToast(message: string): void {
    if (this.toastTimer !== undefined) window.clearTimeout(this.toastTimer);
    this.toast.textContent = message;
    this.toast.setAttribute("aria-hidden", "false");
    this.toast.classList.add("is-visible");
    this.toastTimer = window.setTimeout(() => {
      this.toast.classList.remove("is-visible");
      this.toast.setAttribute("aria-hidden", "true");
    }, 1800);
  }

  renderPerformance(devMode: boolean): void {
    if (!devMode) return;
    this.performancePanel.hidden = false;
    const lines: Array<[string, number | undefined]> = [
      ["HOME LOW", this.monitor.get("boot-to-home-low")],
      ["INTERACTIVE", this.monitor.get("boot-to-interactive")],
      ["HOME HIGH", this.monitor.get("interactive-to-home-high")],
      ["FLAPPY READY", this.monitor.get("interactive-to-flappy-ready")],
    ];
    this.performancePanel.innerHTML = lines
      .map(([label, value]) => `<span><b>${label}</b><strong>${value === undefined ? "--" : `${value.toFixed(0)}ms`}</strong></span>`)
      .join("");
  }

  destroy(): void {
    if (this.toastTimer !== undefined) window.clearTimeout(this.toastTimer);
    this.listeners.forEach((remove) => remove());
    this.element.remove();
  }
}
